import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileDown, Loader2 } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field =
  "w-full bg-transparent border-b border-ivory/20 focus:border-gold outline-none py-3 text-ivory placeholder:text-ivory/30 transition-colors duration-300";

const PAGES = ["Who the program is for", "Our four-chapter approach", "Services & BRRRR concierge", "Private lending, explained", "Next steps & contact"];

export const InvestorDeck = () => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [emailed, setEmailed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/deck-requests`, { ...form, site_url: window.location.origin });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      setDone(url);
      setEmailed(!!data.email_sent);
      window.open(url, "_blank", "noopener");
      toast.success("Your investor overview is ready.", { description: data.email_sent ? "We've also emailed you a copy." : "Kenneth will follow up personally." });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast.error(status < 500 && typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="deck" className="relative py-28 lg:py-36 bg-ink overflow-hidden" data-testid="deck-section">
      <div className="pointer-events-none absolute right-[-15%] top-[-20%] h-[520px] w-[520px] rounded-full bg-gold/10 blur-[160px]" />
      <div className="mx-auto max-w-[1440px] px-6 lg:px-12 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-6">
          <SectionHead index="06" eyebrow="Investor Overview" title={<>The program, on <em className="not-italic text-gold">six pages.</em></>} />
          <Reveal delay={0.2}>
            <p className="mt-8 text-ivory/60 leading-relaxed max-w-md">
              A short PDF overview of the Bossard investor program — what we do, how the four chapters work, and what private lending looks like when it's done properly.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <ol className="mt-8 space-y-3" data-testid="deck-contents">
              {PAGES.map((p, i) => (
                <li key={p} className="flex items-center gap-4 text-sm text-ivory/70">
                  <span className="font-mono text-[10px] text-gold">0{i + 2}</span>
                  <span className="h-px w-5 bg-gold/50" />
                  {p}
                </li>
              ))}
            </ol>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-5 lg:col-start-8">
          <div className="relative border border-gold/25 bg-obsidian p-8 lg:p-10">
            <div className="absolute -inset-px border border-gold/15 translate-x-3 translate-y-3 pointer-events-none" />
            <div className="relative">
              <FileDown size={22} strokeWidth={1.25} className="text-gold" />
              <h3 className="mt-5 font-serif text-2xl lg:text-3xl text-ivory">Get the investor overview</h3>
              {done ? (
                <div className="mt-6" data-testid="deck-success">
                  <p className="text-ivory/70 text-sm leading-relaxed">
                    Thank you. Your download should have opened in a new tab{emailed ? "; a copy is also on its way to your inbox" : ""}. Kenneth will follow up personally within one business day.
                  </p>
                  <a href={done} target="_blank" rel="noreferrer" className="btn-gold mt-6 inline-flex items-center gap-3 bg-gold text-obsidian text-xs tracking-[0.2em] uppercase px-7 py-4" data-testid="deck-download-link">
                    Open the PDF <FileDown size={14} />
                  </a>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-6" data-testid="deck-form">
                  <label className="block">
                    <span className="eyebrow">Name</span>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name (optional)" className={field} data-testid="deck-name-input" />
                  </label>
                  <label className="block">
                    <span className="eyebrow">Email</span>
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className={field} data-testid="deck-email-input" />
                  </label>
                  <button type="submit" disabled={busy} className="btn-gold w-full inline-flex items-center justify-center gap-3 bg-gold text-obsidian text-xs tracking-[0.2em] uppercase px-7 py-4 disabled:opacity-60" data-testid="deck-submit-btn">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />}
                    {busy ? "Preparing" : "Download the PDF"}
                  </button>
                  <p className="text-[11px] text-ivory/35 leading-relaxed">We may follow up personally. No spam, ever.</p>
                </form>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
